import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Radio, RefreshCw, CheckCircle2, XCircle,
  Wifi, WifiOff, Server, Search, ArrowUpCircle,
  Loader2, Upload, History, X, Package,
  Filter, Pencil, Trash2, RotateCcw, ChevronDown,
} from "lucide-react";
import "./OTA.css";
import useModulePermissions, { FLEET_PERMISSION_IDS } from "../Common/hooks/useModulePermissions";


import { getData, postDataWithFiles, putData, deleteDataById, postDataWithBody } from "../../../Services/Services";
import { toast } from "react-toastify";
import {
  otaGetDevices, otaGetFirmwares,
  otaGetUpdateHistory,
  otaUploadFirmware,
  otaRescheduleFirmware,
  otaDeleteByDeviceId,
  otaRevertFirmware,
} from "../../../Services/EndPoints";

/* ─────────────────────── HELPERS ─────────────────────── */
const formatAbsoluteDateTime = (ts) => {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d)) return "—";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const year = d.getFullYear();
  const minutes = String(d.getMinutes()).padStart(2, "0");
  const hours = d.getHours() % 12 || 12;
  const ampm = d.getHours() >= 12 ? "PM" : "AM";
  return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
};


// Parse Java LocalDateTime array [year, month(1-based), day, hour, min, sec, nano]
const parseJavaDateTime = (val) => {
  if (!val) return null;
  if (Array.isArray(val) && val.length >= 6) {
    return new Date(val[0], val[1] - 1, val[2], val[3], val[4], val[5], val[6] ? Math.floor(val[6] / 1e6) : 0);
  }
  const d = new Date(val);
  return isNaN(d) ? null : d;
};

// Latest firmware = most recently uploaded (highest createdAt timestamp)
const getLatestFW = (deviceId, firmwares) => {
  const list = firmwares.filter((f) => f.deviceId === deviceId);
  if (!list.length) return null;
  return list.reduce((best, fw) =>
    !best || (fw.createdAt || 0) > (best.createdAt || 0) ? fw : best, null
  );
};


/* ─────────────────────── SUB-COMPONENTS ─────────────────────── */
const StatCard = ({ icon, label, value, accent, sub }) => (
  <div className={`ota-stat-card ota-stat-${accent}`}>
    <div className="ota-stat-icon">{icon}</div>
    <div className="ota-stat-body">
      <span className="ota-stat-value">{value}</span>
      <span className="ota-stat-label">{label}</span>
      {sub && <span className="ota-stat-sub">{sub}</span>}
    </div>
  </div>
);

const StatusBadge = ({ isOnline }) => {
  if (isOnline === true)
    return (
      <span className="ota-badge ota-badge-online">
        <span className="ota-pulse-dot" /> Online
      </span>
    );
  if (isOnline === false)
    return (
      <span className="ota-badge ota-badge-offline">
        <WifiOff size={11} /> Offline
      </span>
    );
  return (
    <span className="ota-badge ota-badge-offline">
      <XCircle size={11} /> Unknown
    </span>
  );
};

// const UpdateProgressCell = ({ device, latestFW }) => {
//   if (!latestFW)
//     return <span className="ota-no-progress">No FW assigned</span>;

//   if (device.currentVersion === latestFW.firmwareVersion && latestFW.updateStatus === "SUCCESS")
//     return (
//       <span className="ota-progress-status ota-progress-completed">
//         <CheckCircle2 size={13} /> Completed
//       </span>
//     );

//   if (latestFW.updateStatus === "SUCCESS")
//     return (
//       <span className="ota-progress-status ota-progress-completed">
//         <CheckCircle2 size={13} /> Completed
//       </span>
//     );

//   if (latestFW.updateStatus === "FAILED")
//     return (
//       <span className="ota-progress-status ota-progress-failed">
//         <XCircle size={13} /> Failed
//       </span>
//     );

//   return <span className="ota-progress-status ota-progress-pending">Pending</span>;
// };

/* ─────────────────────── UPLOAD MODAL ─────────────────────── */
const localDateTimeMin = () => {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const UploadFirmwareModal = ({ devices, firmwares, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [rolloutType, setRolloutType] = useState("SINGLE_DEVICE");
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [scheduledTime, setScheduledTime] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [updatingError, setUpdatingError] = useState("");
  const [success, setSuccess] = useState(false);
  const fileRef = useRef(null);

  const isDeviceUpdating = (deviceId) => {
    const fw = getLatestFW(deviceId, firmwares);
    return fw && (fw.updateStatus === "UPDATING" || fw.updateStatus === "UPDATEING");
  };

  const toggleDevice = (deviceId) => {
    setError("");
    setUpdatingError("");
    if (rolloutType === "SINGLE_DEVICE") {
      setSelectedDevices((prev) =>
        prev.includes(deviceId) ? [] : [deviceId]
      );
    } else {
      setSelectedDevices((prev) =>
        prev.includes(deviceId)
          ? prev.filter((d) => d !== deviceId)
          : [...prev, deviceId]
      );
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) { setError("Please select a .bin file."); return; }
    if (!version.trim()) { setError("Please enter a firmware version."); return; }
    if (!/^\d+(\.\d+)*$/.test(version.trim())) { setError("Version must follow the format 1.0.0 (e.g. 1.2.3)."); return; }
    if (!scheduledTime) { setError("Please select a schedule date and time."); return; }
    if (new Date(scheduledTime) < new Date()) { setError("Scheduled time must be current or a future date."); return; }
    if (selectedDevices.length === 0) { setError("Please select at least one device."); return; }

    setUploading(true);
    setError("");
    const formData = new FormData();
    formData.append("file", file);
    formData.append("deviceIds", selectedDevices.join(","));
    formData.append("version", version.trim());
    formData.append("rolloutType", rolloutType);
    if (scheduledTime) {
      formData.append("scheduledTime", scheduledTime.replace("T", " ") + ":00");
    }

    try {
      const data = await postDataWithFiles(otaUploadFirmware, formData);
      if (data && data.status === "success") {
        setSuccess(true);
        setTimeout(() => { onSuccess(); onClose(); }, 1500);
      } else {
        setError((data && data.msg) || "Upload failed. Please try again.");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="ota-modal-overlay" onClick={onClose}>
      <div className="ota-modal ota-upload-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ota-modal-header">
          <Upload size={20} />
          <h3>Upload New Firmware</h3>
          <button className="ota-modal-close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="ota-upload-success">
            <CheckCircle2 size={40} />
            <p>Firmware uploaded successfully!</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="ota-upload-form">
              {/* File */}
              <div className="ota-form-group">
                <label className="ota-form-label">Firmware File (.bin) <span style={{ color: "red" }}>*</span></label>
                <div
                  className="ota-file-drop"
                  onClick={() => fileRef.current?.click()}
                >
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".bin"
                    onChange={(e) => { setFile(e.target.files[0]); setError(""); }}
                    style={{ display: "none" }}
                  />
                  {file ? (
                    <span className="ota-file-chosen">
                      <Package size={15} /> {file.name}
                    </span>
                  ) : (
                    <span className="ota-file-placeholder">
                      <Upload size={15} /> Click to select .bin file
                    </span>
                  )}
                </div>
              </div>

              {/* Version */}
              <div className="ota-form-group">
                <label className="ota-form-label">Version <span style={{ color: "red" }}>*</span></label>
                <input
                  type="text"
                  placeholder="e.g. V1.0.0"
                  value={version}
                  onChange={(e) => { setVersion(e.target.value); setError(""); }}
                  className="ota-form-input"
                />
              </div>

              {/* Rollout Type */}
              <div className="ota-form-group">
                <label className="ota-form-label">Rollout Type</label>
                <select
                  value={rolloutType}
                  onChange={(e) => {
                    const type = e.target.value;
                    setRolloutType(type);
                    setSelectedDevices(type === "MASS_SELECTION" ? devices.map((d) => d.deviceId) : []);
                    setError("");
                  }}
                  className="ota-form-select"
                >
                  <option value="SINGLE_DEVICE">Single Device</option>
                  <option value="MASS_SELECTION">Mass Selection</option>
                </select>
              </div>

              {/* Schedule Date & Time */}
              <div className="ota-form-group">
                <label className="ota-form-label">
                  Schedule Date &amp; Time <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="datetime-local"
                  value={scheduledTime}
                  min={localDateTimeMin()}
                  onFocus={(e) => { e.target.min = localDateTimeMin(); }}
                  onChange={(e) => { setScheduledTime(e.target.value); setError(""); }}
                  className="ota-form-input"
                  required
                />
              </div>

              {/* Device Selection */}
              <div className="ota-form-group">
                <label className="ota-form-label">
                  Select Devices <span style={{ color: "red" }}>*</span>{" "}
                  <span className="ota-selected-count">
                    {selectedDevices.length} selected
                  </span>
                </label>
                <div className="ota-device-select-list">
                  {rolloutType === "MASS_SELECTION" && (
                    <label className={`ota-device-option ota-device-select-all${selectedDevices.length === devices.filter((d) => !isDeviceUpdating(d.deviceId)).length && devices.filter((d) => !isDeviceUpdating(d.deviceId)).length > 0 ? " ota-device-select-all--active" : ""}`}>
                      <input
                        type="checkbox"
                        checked={selectedDevices.length === devices.filter((d) => !isDeviceUpdating(d.deviceId)).length && devices.filter((d) => !isDeviceUpdating(d.deviceId)).length > 0}
                        onChange={() => {
                          setError("");
                          setUpdatingError("");
                          const selectableIds = devices.filter((d) => !isDeviceUpdating(d.deviceId)).map((d) => d.deviceId);
                          if (selectedDevices.length === selectableIds.length) {
                            setSelectedDevices([]);
                          } else {
                            setSelectedDevices(selectableIds);
                          }
                        }}
                      />
                      <span className="ota-device-option-id" style={{ fontWeight: 700 }}>Select All</span>
                      <span className="ota-device-option-name" style={{ color: "#64748b" }}>{devices.length} devices</span>
                    </label>
                  )}
                  {devices.map((d) => {
                    const updating = isDeviceUpdating(d.deviceId);
                    return (
                      <label
                        key={d.deviceId}
                        className={`ota-device-option${updating ? " ota-device-option--disabled" : ""}`}
                        onClick={updating ? () => setUpdatingError(`Device ${d.deviceId} is currently updating. Please wait until the update completes.`) : undefined}
                      >
                        <input
                          type={rolloutType === "SINGLE_DEVICE" ? "radio" : "checkbox"}
                          checked={selectedDevices.includes(d.deviceId)}
                          onChange={() => toggleDevice(d.deviceId)}
                          disabled={updating}
                        />
                        <span className="ota-device-option-id">{d.deviceId}</span>
                        <span className="ota-device-option-name">{d.deviceName}</span>
                        <span className="ota-device-option-ver">
                          {updating ? (
                            <span style={{ color: "#f59e0b", fontSize: "11px", fontWeight: 600 }}>Updating…</span>
                          ) : (
                            <>
                              {d.currentVersion || "—"}
                              {(() => {
                                const latestFW = getLatestFW(d.deviceId, firmwares);
                                if (latestFW && latestFW.firmwareVersion && latestFW.firmwareVersion !== d.currentVersion) {
                                  return (
                                    <span style={{ marginLeft: 4, color: "#1a7a73", fontSize: "11px" }}>
                                      → {latestFW.firmwareVersion}
                                    </span>
                                  );
                                }
                                return null;
                              })()}
                            </>
                          )}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {updatingError && <div className="ota-upload-error ota-upload-error--warn">{updatingError}</div>}
              {error && <div className="ota-upload-error">{error}</div>}
            </div>

            <div className="ota-modal-actions">
              <button
                type="submit"
                className="ota-btn ota-btn-primary"
                disabled={uploading}
              >
                {uploading ? (
                  <><Loader2 size={14} className="ota-spin" /> Uploading…</>
                ) : (
                  <><Upload size={14} /> Upload Firmware</>
                )}
              </button>
              <button
                type="button"
                className="ota-btn ota-btn-ghost"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

/* ─────────────────────── RESCHEDULE MODAL ─────────────────────── */
const RescheduleFirmwareModal = ({ target, onClose, onSuccess }) => {
  const [scheduledTime, setScheduledTime] = useState(() => {
    if (!target.scheduledTime) return "";
    const d = new Date(target.scheduledTime);
    return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleSave = async () => {
    if (!scheduledTime) { setError("Please select a date and time."); return; }
    if (new Date(scheduledTime) < new Date()) { setError("Scheduled time must be a future date."); return; }

    setSaving(true);
    setError("");
    const endpoint = otaRescheduleFirmware.replace("FIRMWARE_ID", target.firmwareId);
    try {
      const data = await putData(endpoint, {
        scheduledTime: scheduledTime.replace("T", " ") + ":00",
      });
      if (data && data.status === "success") {
        toast.success(data.msg || "Scheduled time updated successfully.", { position: "top-center" });
        onSuccess();
        onClose();
      } else {
        setError((data && data.msg) || "Reschedule failed. Please try again.");
      }
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ota-modal-overlay" onClick={onClose}>
      <div className="ota-modal ota-reschedule-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ota-modal-header">
          <Pencil size={18} />
          <h3>Reschedule Firmware Update</h3>
          <button className="ota-modal-close" onClick={onClose} type="button">
            <X size={18} />
          </button>
        </div>
        <div className="ota-reschedule-body">
          <div className="ota-form-group">
            <label className="ota-form-label">
              New Schedule Date &amp; Time <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="datetime-local"
              value={scheduledTime}
              min={localDateTimeMin()}
              onFocus={(e) => { e.target.min = localDateTimeMin(); }}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="ota-form-input"
            />
          </div>
          {error && <div className="ota-upload-error">{error}</div>}
        </div>
        <div className="ota-modal-actions">
          <button
            className="ota-btn ota-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? <><Loader2 size={14} className="ota-spin" /> Saving…</> : "Save"}
          </button>
          <button className="ota-btn ota-btn-ghost" onClick={onClose} type="button">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── REVERT CONFIRM MODAL ─────────────────────── */
const RevertConfirmModal = ({ target, onClose, onConfirm, reverting }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!reason.trim()) { setError("Please enter a reason for reverting."); return; }
    onConfirm(reason.trim());
  };

  return (
    <div className="ota-modal-overlay" onClick={onClose}>
      <div className="ota-modal ota-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ota-modal-header">
          <RotateCcw size={18} style={{ color: "#d97706" }} />
          <h3>Revert Firmware Version</h3>
          <button className="ota-modal-close" onClick={onClose} type="button" disabled={reverting}>
            <X size={18} />
          </button>
        </div>
        <div className="ota-confirm-body">
          {/* <div className="ota-confirm-icon ota-confirm-icon--warn">
            <RotateCcw size={32} />
          </div> */}
          <p className="ota-confirm-msg">
            Revert firmware {target.firmwareVersion && <> from <strong>v{target.firmwareVersion}</strong></>} for device <strong>{target.deviceId}</strong>
            
          </p>
          <p className="ota-confirm-sub" style={{ marginBottom: 14 }}>
            The device will roll back to its previous firmware version.
          </p>
          <div className="ota-form-group" style={{ textAlign: "left" }}>
            <label className="ota-form-label">
              Reason <span style={{ color: "red" }}>*</span>
            </label>
            <textarea
              className="ota-revert-reason"
              placeholder="Enter reason for reverting..."
              value={reason}
              rows={3}
              maxLength={150}
              onChange={(e) => {
                const sanitized = e.target.value.replace(/[^a-zA-Z0-9 .,\-_()]/g, "");
                setReason(sanitized);
                setError("");
              }}
            />
            <div className="ota-reason-counter" style={{ color: reason.length >= 150 ? "#dc2626" : "#94a3b8" }}>
              {reason.length} / 150
            </div>
            {error && <div className="ota-upload-error" style={{ marginTop: 6 }}>{error}</div>}
          </div>
        </div>
        <div className="ota-modal-actions">
          <button
            className="ota-btn ota-btn-warn"
            onClick={handleConfirm}
            disabled={reverting}
          >
            {reverting
              ? <><Loader2 size={14} className="ota-spin" /> Reverting…</>
              : <><RotateCcw size={14} /> Revert</>}
          </button>
          <button className="ota-btn ota-btn-ghost" onClick={onClose} disabled={reverting}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────── DELETE CONFIRM MODAL ─────────────────────── */
const DeleteConfirmModal = ({ deviceId, firmwareVersion, onConfirm, onClose, deleting }) => (
  <div className="ota-modal-overlay" onClick={onClose}>
    <div className="ota-modal ota-confirm-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ota-modal-header">
        <Trash2 size={18} style={{ color: "#dc2626" }} />
        <h3>Delete Firmware</h3>
        <button className="ota-modal-close" onClick={onClose} type="button">
          <X size={18} />
        </button>
      </div>
      <div className="ota-confirm-body">
        <div className="ota-confirm-icon">
          <Trash2 size={36} />
        </div>
        <p className="ota-confirm-msg">
          Are you sure you want to delete the pending firmware{" "}
          {firmwareVersion && <strong>v{firmwareVersion}</strong>}{" "}
          for device <strong>{deviceId}</strong>?
        </p>
        <p className="ota-confirm-sub">This action cannot be undone.</p>
      </div>
      <div className="ota-modal-actions">
        <button
          className="ota-btn ota-btn-danger"
          onClick={onConfirm}
          disabled={deleting}
        >
          {deleting
            ? <><Loader2 size={14} className="ota-spin" /> Deleting…</>
            : <><Trash2 size={14} /> Delete</>}
        </button>
        <button className="ota-btn ota-btn-ghost" onClick={onClose} type="button" disabled={deleting}>
          Cancel
        </button>
      </div>
    </div>
  </div>
);

/* ─────────────────────── MAIN COMPONENT ─────────────────────── */
const OTAMain = () => {
  // Role's rights for OTA Management (web_permissions from getUserProfile):
  // Edit → Reschedule / Revert, Delete → delete pending firmware
  const { canEdit, canDelete } = useModulePermissions(FLEET_PERMISSION_IDS.OTA_MANAGEMENT);
  const [devices, setDevices] = useState([]);
  const [firmwares, setFirmwares] = useState([]);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 10;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [rescheduleTarget, setRescheduleTarget] = useState(null);
  const [revertTarget, setRevertTarget] = useState(null);
  const [reverting, setReverting] = useState(false);
  const [expandedDevices, setExpandedDevices] = useState({});
  const toggleHistoryDevice = (deviceId) =>
    setExpandedDevices((prev) => ({ ...prev, [deviceId]: !prev[deviceId] }));
  const [deleteConfirmTarget, setDeleteConfirmTarget] = useState(null);
  const [deletingDeviceId, setDeletingDeviceId] = useState(null);

  const handleDeleteFirmware = async () => {
    const deviceId = deleteConfirmTarget.deviceId;
    setDeletingDeviceId(deviceId);
    try {
      const data = await deleteDataById(otaDeleteByDeviceId, deviceId);
      if (data && data.status === "success") {
        toast.success(data.msg || "Firmware deleted successfully.", { position: "top-center" });
        setRefreshKey((k) => k + 1);
      } else {
        toast.error((data && data.msg) || "Delete failed. Please try again.", { position: "top-center" });
      }
    } catch (err) {
      toast.error("Network error: " + err.message, { position: "top-center" });
    } finally {
      setDeletingDeviceId(null);
      setDeleteConfirmTarget(null);
    }
  };
  const handleRevertFirmware = async (reason) => {
    setReverting(true);
    try {
      const endpoint = otaRevertFirmware.replace("DEVICE_ID", revertTarget.imeiNumber);
      const data = await postDataWithBody(
        endpoint,
        { imeiNumber: revertTarget.imeiNumber, rollbackReason: reason },
        { "Content-Type": "application/json" }
      );
      if (data && data.status === "success") {
        toast.success(data.msg || "Firmware reverted successfully.", { position: "top-center" });
        setRefreshKey((k) => k + 1);
        setRevertTarget(null);
      } else {
        toast.error((data && data.msg) || "Revert failed. Please try again.", { position: "top-center" });
      }
    } catch (err) {
      toast.error("Network error: " + err.message, { position: "top-center" });
    } finally {
      setReverting(false);
    }
  };

  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocodedRef = useRef({});

  /* clock */
  useEffect(() => {
    const id = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  /* fetch all data */
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [devData, fwData, histData] = await Promise.all([
        getData(otaGetDevices),
        getData(otaGetFirmwares),
        getData(otaGetUpdateHistory),
      ]);


      setDevices(Array.isArray(devData) ? [...devData].sort((a, b) => a.id - b.id) : []);
      setFirmwares(Array.isArray(fwData) ? fwData : []);
      setUpdateHistory(Array.isArray(histData) ? histData : []);
    } catch (err) {
      console.error("OTA data fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  /* reverse geocode lat,lon → address */
  useEffect(() => {
    const apiKey = 'AIzaSyDLzC-FhQmB8JS2H46OW46DNOEboBbrZVg';
    if (!apiKey || !devices.length) return;

    const toGeocode = devices.filter((d) => {
      if (!d.location) return false;
      const key = typeof d.location === "string"
        ? d.location
        : JSON.stringify(d.location);
      return !geocodedRef.current[key];
    });

    if (!toGeocode.length) return;

    const geocode = async (device) => {
      const loc = device.location;
      let lat, lng;

      if (typeof loc === "string") {
        const parts = loc.split(",");
        if (parts.length >= 2) { lat = parts[0].trim(); lng = parts[1].trim(); }
      } else if (loc && typeof loc === "object") {
        lat = loc.lat ?? loc.latitude;
        lng = loc.lng ?? loc.longitude;
      }

      if (!lat || !lng) return null;
      const key = typeof loc === "string" ? loc : JSON.stringify(loc);
      geocodedRef.current[key] = "loading";

      try {
        const res = await fetch(
          `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=${apiKey}`
        );
        const data = await res.json();
        if (data.results && data.results.length > 0) {
          const parts = data.results[0].formatted_address.split(", ");
          const address = parts.slice(0, 2).join(", ");
          geocodedRef.current[key] = address;
          return { key, address };
        }
      } catch (err) {
        geocodedRef.current[key] = null;
      }
      return null;
    };

    Promise.all(toGeocode.map(geocode)).then((results) => {
      const updates = {};
      results.filter(Boolean).forEach((r) => { updates[r.key] = r.address; });
      if (Object.keys(updates).length) {
        setGeocodedLocations((prev) => ({ ...prev, ...updates }));
      }
    });
  }, [devices]);

  /* auto-refresh every 30 seconds */
  // useEffect(() => {
  //   const id = setInterval(() => setRefreshKey((k) => k + 1), 30000);
  //   return () => clearInterval(id);
  // }, []);

  /* derived stats */
  const total = devices.length;
  const online = devices.filter((d) => d.isOnline === true).length;
  const offline = devices.filter((d) => d.isOnline === false).length;
  // const needsUpdate = devices.filter((d) => {
  //   const fw = getLatestFW(d.deviceId, firmwares);
  //   return fw && d.currentVersion !== fw.firmwareVersion;
  // }).length;

  /* recent update history for sidebar */
  const recentHistory = [...updateHistory]
    .filter((h) => h.status && h.deviceId)
    .sort((a, b) => b.createdAt - a.createdAt);

  /* filtered table rows */
  const filtered = devices.filter((d) => {
    const q = search.toLowerCase();
    const matchSearch =
      !search ||
      d.deviceId.toLowerCase().includes(q) ||
      d.deviceName.toLowerCase().includes(q);
    const matchStatus =
      statusFilter === "all" ||
      (statusFilter === "online" && d.isOnline === true) ||
      (statusFilter === "offline" && d.isOnline !== true);
    return matchSearch && matchStatus;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedRows  = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  /* loading screen */
  if (loading) {
    return (
      <div className="ota-loading">
        <Loader2 size={36} className="ota-spin" />
        <p>Loading…</p>
      </div>
    );
  }

  return (
    <div className="ota-root">
      {/* ── HEADER ── */}
      <div className="ota-header">
        <div className="ota-header-left">
          <div className="ota-header-icon-wrap">
            <Radio size={22} />
          </div>
          <div>
            <h2 className="ota-title">OTA Management</h2>
            <span className="ota-subtitle">
              {/* Dashboard Overview &nbsp;·&nbsp;{" "} */}
              {formatAbsoluteDateTime(currentTime)}
            </span>
          </div>
        </div>
        <div className="ota-header-right">
          {/* <div className="ota-live-badge">
            <span className="ota-live-dot" /> LIVE
          </div> */}
          <button
            className="ota-btn ota-btn-ghost"
            onClick={() => setRefreshKey((k) => k + 1)}
          >
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            className="ota-btn ota-btn-primary"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={15} /> Upload New Firmware
          </button>
        </div>
      </div>

      <div className="ota-main-panel">
        {/* ── LEFT: cards + table ── */}
        <div className="ota-left-section">
          {/* ── STAT CARDS ── */}
          <div className="ota-stats-row">
            <StatCard
              icon={<Server size={20} />}
              label="Total Devices"
              value={total}
              accent="blue"
              sub="Registered in fleet"
            />
            <StatCard
              icon={<Wifi size={20} />}
              label="Online"
              value={online}
              accent="green"
              sub={`available devices`}
            />
            <StatCard
              icon={<WifiOff size={20} />}
              label="Offline"
              value={offline}
              accent="red"
              sub="Unreachable devices"
            />
          </div>
          <div className="ota-table-section">
          {/* Toolbar */}
          <div className="ota-toolbar">
            <div className="ota-toolbar-left">
              <h3 className="ota-table-title">
                Device OTA Details
              </h3>
            </div>
            <div className="ota-toolbar-right">
              <div className="ota-search-wrap">
                <Search size={15} className="ota-search-icon" />
                <input
                  className="ota-search-input"
                  placeholder="Search device ID or name…"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="ota-filter-wrap">
                <Filter size={13} className="ota-filter-icon" />
                <select
                  className="ota-filter-select"
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                >
                  <option value="all">All Status</option>
                  <option value="online">Online</option>
                  <option value="offline">Offline</option>
                </select>
              </div>
              <span className="ota-count-label">
                {filtered.length} / {total} devices
              </span>
            </div>
          </div>

          {/* Table */}
          <div className="ota-table-wrap">
            <table className="ota-table">
              <thead>
                <tr>
                  <th>Device ID</th>
                  {/* <th>Device Name</th> */}
                  <th>Device Status</th>
                  <th>Current Version</th>
                  {/* <th>Upload Date</th> */}
                  <th>Latest Version</th>
                  <th>Version Status</th>
                  <th>Schedule Date & Time</th>
                   <th>Version Upload Date & Time</th>
                  <th>Last Seen</th>
                  <th>Location</th>
                  <th>Action</th>

                  {/* <th>Signal Strength</th> */}

                </tr>
              </thead>
              <tbody>
                {pagedRows.map((device) => {
                  const latestFW = getLatestFW(device.deviceId, firmwares);
                  const upToDate =
                    latestFW &&
                    device.currentVersion === latestFW.firmwareVersion &&
                    latestFW.updateStatus === "SUCCESS";
                  return (
                    <tr
                      key={device.id}
                      className={`ota-row ${upToDate ? "" : "ota-row-needs-update"}`}
                    >
                      <td className="ota-device-id">{device.deviceId}</td>
                      {/* <td>
                        <div className="ota-device-name-cell">
                          {device.deviceName}
                        </div>
                      </td> */}
                      <td>
                        <StatusBadge isOnline={device.isOnline} />
                      </td>
                      <td>
                        {device.currentVersion
                          ? <span className={upToDate ? "ota-fw-ok" : "ota-fw-old"}>{device.currentVersion}</span>
                          : <span className="ota-no-progress">NA</span>}
                      </td>
                      {/* <td className="ota-last-seen">
                        {device.createdAt
                          ? new Date(device.createdAt).toLocaleDateString("en-IN", {
                            day: "2-digit", month: "short", year: "numeric",
                          })
                          : "—"}
                      </td> */}
                      <td>
                        {!latestFW ? (
                          <span className="ota-no-progress">NA</span>
                        ) : upToDate ? (
                          <span className="ota-update-status ota-update-none">
                            <CheckCircle2 size={13} /> Updated
                          </span>
                        ) : (
                          <span className="ota-update-status ota-update-available">
                            <ArrowUpCircle size={13} />
                            <span className="ota-version-compare">
                              {/* v{device.currentVersion}
                              <span className="ota-version-arrow"> → </span> */}
                              {latestFW.firmwareVersion}
                            </span>
                          </span>
                        )}
                      </td>
                      <td className="ota-last-seen">
                        {latestFW
                          ? latestFW.updateStatus === "SUCCESS"
                            ? <span className="ota-fw-ok">Completed</span>
                            : latestFW.updateStatus === "FAILED"
                              ? <span className="ota-fw-old">Failed</span>
                              : (latestFW.updateStatus === "UPDATING" || latestFW.updateStatus === "UPDATEING")
                                ? <span className="ota-status-updating"><Loader2 size={12} className="ota-spin" /> Updating</span>
                                : <span className="ota-no-progress">Pending</span>
                          : <span className="ota-no-progress">NA</span>
                        }
                      </td>
                     
                      <td className="ota-last-seen">
                        {latestFW?.scheduledTime ? formatAbsoluteDateTime(latestFW.scheduledTime) : "—"}
                      </td>
                      <td className="ota-last-seen">
                        {latestFW?.updateStatus === "SUCCESS" && latestFW?.lastUpdatedAt
                          ? (() => {
                              const d = parseJavaDateTime(latestFW.lastUpdatedAt);
                              return d ? formatAbsoluteDateTime(d) : "—";
                            })()
                          : <span className="ota-no-progress">—</span>}
                      </td>
                      <td className="ota-last-seen">{formatAbsoluteDateTime(device.lastSeen)}</td>
                      <td className="ota-location">
                        {device.location
                          ? (() => {
                              const key = typeof device.location === "string"
                                ? device.location
                                : JSON.stringify(device.location);
                              const addr = geocodedLocations[key];
                              if (addr) return addr;
                              if (geocodedRef.current[key] === "loading")
                                return <span style={{ color: "#94a3b8", fontSize: "11px" }}>Fetching…</span>;
                              return <span style={{ color: "#94a3b8" }}>{device.location}</span>;
                            })()
                          : "—"}
                      </td>
                      <td>
                        {latestFW &&
                          latestFW.updateStatus !== "SUCCESS" &&
                          latestFW.updateStatus !== "FAILED" &&
                          latestFW.updateStatus !== "UPDATEING" &&
                          latestFW.updateStatus !== "UPDATING" ? (
                          <div className="ota-action-cell">
                            {!canEdit && !canDelete && <span className="ota-no-progress">—</span>}
                            {canEdit && <button
                              className="ota-edit-schedule-btn"
                              title="Reschedule"
                              onClick={() => setRescheduleTarget({
                                firmwareId: latestFW.id,
                                scheduledTime: latestFW.scheduledTime,
                              })}
                            >
                              <Pencil size={13} />
                            </button>}
                            {canDelete && <button
                              className="ota-delete-btn"
                              title="Delete pending firmware"
                              disabled={deletingDeviceId === device.deviceId}
                              onClick={() => setDeleteConfirmTarget({ deviceId: device.deviceId, firmwareVersion: latestFW?.firmwareVersion })}
                            >
                              {deletingDeviceId === device.deviceId
                                ? <Loader2 size={13} className="ota-spin" />
                                : <Trash2 size={13} />}
                            </button>}
                          </div>
                        ) : latestFW?.updateStatus === "SUCCESS" ? (
                          <div className="ota-action-cell">
                            {canEdit ? <button
                              className="ota-revert-btn"
                              title="Revert firmware version"
                              onClick={() => setRevertTarget({ deviceId: device.deviceId, imeiNumber: device.imeiNumber, firmwareVersion: latestFW?.firmwareVersion })}
                            >
                              <RotateCcw size={13} />
                            </button> : <span className="ota-no-progress">—</span>}
                          </div>
                        ) : !latestFW ? (
                          <div className="ota-action-cell">
                            <button
                              className="ota-upload-hint-btn"
                              title="Upload new firmware version"
                              onClick={() => setShowUploadModal(true)}
                            >
                              <Upload size={13} />
                            </button>
                          </div>
                        ) : (
                          <span className="ota-no-progress">—</span>
                        )}
                      </td>

                      {/* <td>
                        {device.signalStrength != null
                          ? <span className={
                              device.signalStrength > 70
                                ? "ota-sig-good"
                                : device.signalStrength > 40
                                ? "ota-sig-mid"
                                : "ota-sig-low"
                            }>
                              {device.signalStrength}%
                            </span>
                          : <span className="ota-no-progress">—</span>}
                      </td> */}

                    </tr>
                  );
                })}
                {pagedRows.length === 0 && (
                  <tr>
                    <td colSpan={10} className="ota-empty">
                      No devices match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            </div>{/* end ota-table-wrap */}

            {/* ── PAGINATION ── */}
            {totalPages > 1 && (
              <div className="ota-pagination">
                <button
                  className="ota-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >«</button>
                <button
                  className="ota-page-btn"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >‹</button>

                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                  .reduce((acc, p, idx, arr) => {
                    if (idx > 0 && p - arr[idx - 1] > 1)
                      acc.push(<span key={`ellipsis-${p}`} className="ota-page-ellipsis">…</span>);
                    acc.push(
                      <button
                        key={p}
                        className={`ota-page-btn ${p === currentPage ? "ota-page-active" : ""}`}
                        onClick={() => setCurrentPage(p)}
                      >{p}</button>
                    );
                    return acc;
                  }, [])}

                <button
                  className="ota-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >›</button>
                <button
                  className="ota-page-btn"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >»</button>

                <span className="ota-page-info">
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
              </div>
            )}
          </div>{/* end ota-table-section */}
        </div>{/* end ota-left-section */}

        {/* ── RIGHT: Update History ── */}
        <div className="ota-history-wrapper">
        <div className="ota-update-history-panel">
          <div className="ota-panel-header">
            <History size={15} />
            <span>Update History</span>
          </div>
          <div className="ota-history-list">
            {recentHistory.length === 0 && (
              <div className="ota-panel-empty">No history available</div>
            )}
            {Object.entries(
              recentHistory.reduce((acc, h) => {
                if (!acc[h.deviceId]) acc[h.deviceId] = [];
                acc[h.deviceId].push(h);
                return acc;
              }, {})
            ).map(([deviceId, items]) => (
              <div key={deviceId} className="ota-history-group">
                <button
                  className="ota-history-group-header"
                  onClick={() => toggleHistoryDevice(deviceId)}
                >
                  <span className="ota-history-group-id">{deviceId}</span>
                  <span className="ota-history-group-count">{items.length}</span>
                  <ChevronDown
                    size={14}
                    className={`ota-history-chevron ${expandedDevices[deviceId] ? "ota-chevron-open" : ""}`}
                  />
                </button>
                {expandedDevices[deviceId] && (
                  <div className="ota-history-group-items">
                    {items.map((h) => (
                      <div key={h.id} className="ota-history-item">
                        <span
                          className={`ota-history-dot ${
                            h.status === "SUCCESS"
                              ? "ota-dot-success"
                              : h.status === "UPDATING"
                              ? "ota-dot-updating"
                              : h.status === "ROLLED_BACK"
                              ? "ota-dot-rolledback"
                              : h.status === "PENDING"
                              ? "ota-dot-pending"
                              : "ota-dot-failed"
                          }`}
                        />
                        <div className="ota-history-content">
                          <div className="ota-history-top">
                            <span className="ota-history-fw">FW v{h.firmwareVersion}</span>
                            <span className={
                              h.status === "SUCCESS"
                                ? "ota-text-success"
                                : h.status === "UPDATING"
                                ? "ota-text-updating"
                                : h.status === "ROLLED_BACK"
                                ? "ota-text-rolledback"
                                : h.status === "PENDING"
                                ? "ota-text-pending"
                                : "ota-text-failed"
                            }>
                              {h.status === "SUCCESS" ? "Completed"
                                : h.status === "UPDATING" ? "Updating"
                                : h.status === "ROLLED_BACK" ? "Rolled Back"
                                : h.status === "PENDING" ? "Pending"
                                : "Failed"}
                            </span>
                          </div>
                          {h.message && (
                            <div className="ota-history-message">{h.message}</div>
                          )}
                          <div className="ota-history-bottom">
                            <span className="ota-history-date">{formatAbsoluteDateTime(h.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>{/* end ota-update-history-panel */}
        </div>{/* end ota-history-wrapper */}
      </div>{/* end ota-main-panel */}

      {/* ── UPLOAD MODAL ── */}
      {showUploadModal && (
        <UploadFirmwareModal
          devices={devices}
          firmwares={firmwares}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}

      {/* ── REVERT CONFIRM MODAL ── */}
      {revertTarget && (
        <RevertConfirmModal
          target={revertTarget}
          reverting={reverting}
          onConfirm={handleRevertFirmware}
          onClose={() => { if (!reverting) setRevertTarget(null); }}
        />
      )}

      {/* ── DELETE CONFIRM MODAL ── */}
      {deleteConfirmTarget && (
        <DeleteConfirmModal
          deviceId={deleteConfirmTarget.deviceId}
          firmwareVersion={deleteConfirmTarget.firmwareVersion}
          deleting={deletingDeviceId === deleteConfirmTarget.deviceId}
          onConfirm={handleDeleteFirmware}
          onClose={() => { if (!deletingDeviceId) setDeleteConfirmTarget(null); }}
        />
      )}

      {/* ── RESCHEDULE MODAL ── */}
      {rescheduleTarget && (
        <RescheduleFirmwareModal
          target={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onSuccess={() => setRefreshKey((k) => k + 1)}
        />
      )}
    </div>
  );
};

export default OTAMain;
